import React, { useState } from 'react';
import styled from 'styled-components';

import { useMarketTickerQuery } from '#/api/queries/tickersQueries';
import ExpandableArrow from '#/components/basic/icons/ExpandableArrow';
import RouterLink from '#/components/basic/Link/RouterLink';
import NumberColored from '#/components/basic/text/NumberColored';
import MetaTags from '#/components/layout/header/MetaTags';
import FundingCountdown from '#/components/positions/funding/FundingCountdown';
import {
  formatPercentage,
  formatNumberToSignificantDigits,
  formatPrice,
  formatUsdWithSign,
} from '#/lib/display/formatting';
import { useSelectMarketTickSize } from '#/reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from '#/reduxState/store';

const StatsBlock = styled.div`
  padding: 5px 4px;
  line-height: 1.5;
`;

const DESKTOP_CLOSED_HEIGHT_PX = 43;
const MOBILE_CLOSED_HEIGHT_PX = 52;

const DesktopWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-around;
`;

const MobileWrap = styled.div<{ $expanded: boolean }>`
  width: calc(100% - 8px);
  margin: 0 4px;
  z-index: 1234;
  position: absolute;
  background: var(--elevation-light);
  border-radius: 2px;
  ${({ $expanded }) => ($expanded ? 'background: var(--mainDark);' : '')}
  height: ${({ $expanded }) =>
    $expanded ? 'initial' : `${MOBILE_CLOSED_HEIGHT_PX}px`};

  box-shadow: ${({ $expanded }) =>
    $expanded ? '0 10px 24px 4px var(--elevation-light)' : 'initial'};

  transition: background 0.5s ease-out;
  display: grid;
  padding: 0 8px;
  grid-template-columns: 1fr 0.8fr 0.8fr 1fr max-content;
  grid-column-gap: 4px;
  grid-template-areas:
    'price  fundd  fundd  volBase arrow'
    'index  opInt  opInt  volume  volume'
    'mktLnk mktLnk mktLnk mktLnk  mktLnk';
`;

const StatsBlockMobile = styled(StatsBlock)<{
  $area: string;
  $textRight?: boolean;
}>`
  grid-area: ${({ $area }) => $area || 'initial'};
  padding: 10px 1px;
  font-size: var(--small);
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ $textRight }) => ($textRight ? 'text-align: right;' : '')}
  > div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const StatLabel = styled.div`
  color: var(--gray4b);
  white-space: nowrap;
  text-transform: uppercase;
  font-size: var(--smallest);
`;

const StatLabelMobile = styled(StatLabel)`
  padding-bottom: 0;
  font-size: var(--smallest);
`;

// const StatDetailGreen = styled.div`
//   color: var(--green4);
//   font-family: var(--digitsFontFamily);
// `;

const MobileInnerLink = styled.div`
  text-align: center;
  padding: 4px 24px;
  border-radius: 3px;
  border: 1px solid var(--separator);
`;

const StatDetailRed = styled.div`
  color: var(--red3);
  font-family: var(--digitsFontFamily);
`;

const StatValue = styled.div`
  color: var(--white);
  font-family: var(--digitsFontFamily);
`;

const FundingText = styled.span`
  color: var(--mainLight);
`;

const LoadingPlaceholder = styled.div`
  background: var(--elevation-light);
  border-radius: 2px;
`;

const White = styled.span`
  color: var(--white);
`;

function MarketHeaderStats() {
  const isMobile = useSelector(state => state.screen.isMobile);
  const currentMarket = useSelector(state => state.markets.currentMarket);
  const tickSize = useSelectMarketTickSize(currentMarket.pair);
  const { data: ticker } = useMarketTickerQuery({
    market: currentMarket.pair,
    websocketSubscribe: false,
  });
  const { base } = currentMarket;

  const [mobileExpanded, setMobileExpanded] = useState(false);

  if (!ticker) {
    return (
      <LoadingPlaceholder
        style={{
          height: isMobile ? MOBILE_CLOSED_HEIGHT_PX : DESKTOP_CLOSED_HEIGHT_PX,
        }}
      />
    );
  }

  const tickerChange = formatPercentage(ticker.percentChange) + '%';

  // const low24h = ticker.low && tickSize && formatNumber(ticker.low, { fixedDecimals: tickSize.visibleDecimals });
  // const high24h =ticker.high &&tickSize && formatNumber(ticker.high, { fixedDecimals: tickSize.visibleDecimals });

  // Index feed does not update while an intermittent market is closed - the
  // last known index price keeps being displayed
  const indexPrice =
    tickSize &&
    ticker.indexPrice &&
    formatPrice(ticker.indexPrice, tickSize.visibleDecimals);

  const priceFormatted =
    ticker.close &&
    tickSize &&
    formatPrice(ticker.close, tickSize.visibleDecimals);

  const openInterestQuoteFormatted = formatUsdWithSign(
    ticker.openInterestInQuote,
    {
      decimals: 0,
    }
  );

  const change24h = (
    <>
      {ticker.percentChange !== 0 && (
        <NumberColored
          value={tickerChange}
          color={ticker.percentChange < 0 ? 'red' : 'green'}
        />
      )}
    </>
  );

  const fundingAndCountdown = ticker.nextFundingTime ? (
    <>
      <FundingText>
        {ticker.currentFundingRate
          ? `${formatPrice(Number(ticker.currentFundingRate) * 100, 4)}%`
          : '-'}
      </FundingText>{' '}
      / <FundingCountdown fundingDate={ticker.nextFundingTime} />
    </>
  ) : (
    '-'
  );

  return (
    <>
      {currentMarket.pair && currentMarket.base && currentMarket.quote && (
        <MetaTags
          title={
            currentMarket.pair && tickSize && priceFormatted
              ? `${priceFormatted} ${currentMarket.pair}`
              : ''
          }
          // We do not want to see numbers in Google search results
          shareTitle={currentMarket.pair}
          description={
            // When sharing index page we do not want to override description
            // Buy, sell and trade Idex Token (IDEX-ETH) on the most...
            `Buy, sell and trade ${base || currentMarket.base} (${
              currentMarket.pair
            }) on the world’s most advanced cryptocurrency exchange.`
          }
          mainKeywords={[
            currentMarket.pair,
            currentMarket.base,
            currentMarket.quote,
            base || '',
          ]}
        />
      )}
      {isMobile ? (
        <>
          <div style={{ height: MOBILE_CLOSED_HEIGHT_PX }} />
          <MobileWrap $expanded={mobileExpanded}>
            <StatsBlockMobile $area="price">
              <StatLabelMobile>Price</StatLabelMobile>
              <StatValue>{priceFormatted}</StatValue>
            </StatsBlockMobile>

            <StatsBlockMobile $area="fundd">
              <StatLabelMobile>
                <White>Funding</White> / Countdown
              </StatLabelMobile>
              <StatValue>{fundingAndCountdown}</StatValue>
            </StatsBlockMobile>

            <StatsBlockMobile $area="volBase">
              {/* TODO: volume base */}
              <StatLabelMobile>24H Change</StatLabelMobile>
              <StatValue>{change24h}</StatValue>
            </StatsBlockMobile>

            <ExpandableArrow
              onChange={setMobileExpanded}
              expanded={mobileExpanded}
              marginTop="8px"
              padding="4px 6px"
            />

            {mobileExpanded && (
              <>
                <StatsBlockMobile $area="index">
                  <StatLabelMobile>Index</StatLabelMobile>
                  <StatValue>{indexPrice}</StatValue>
                </StatsBlockMobile>

                <StatsBlockMobile $area="opInt">
                  <StatLabelMobile>Open Interest</StatLabelMobile>
                  <StatValue>{openInterestQuoteFormatted}</StatValue>
                </StatsBlockMobile>

                <StatsBlockMobile $area="volume">
                  <StatLabelMobile>24h Volume</StatLabelMobile>
                  <StatValue>
                    ${formatNumberToSignificantDigits(ticker.quoteVolume, 8)}
                  </StatValue>
                </StatsBlockMobile>

                <StatsBlockMobile $area="mktLnk">
                  <RouterLink to={`/markets/${currentMarket.pair}`}>
                    <MobileInnerLink>Market Details</MobileInnerLink>
                  </RouterLink>
                </StatsBlockMobile>
              </>
            )}
          </MobileWrap>
        </>
      ) : (
        <DesktopWrap>
          <StatsBlock>
            <StatLabel>Price</StatLabel>
            <StatValue>{priceFormatted}</StatValue>
          </StatsBlock>

          <StatsBlock>
            <StatLabel>Index</StatLabel>
            <StatValue>{indexPrice}</StatValue>
          </StatsBlock>

          <StatsBlock>
            <StatLabel>24h Change</StatLabel>
            {change24h}
          </StatsBlock>

          {/* TODO: apply to mobile also */}
          {/* <StatsBlock>
            <StatLabel>24h High</StatLabel>
            <StatDetailGreen>{high24h}</StatDetailGreen>
          </StatsBlock>

          <StatsBlock>
            <StatLabel>24h Low</StatLabel>
            <StatDetailRed>{low24h}</StatDetailRed>
          </StatsBlock> */}

          <StatsBlock>
            <StatLabel>
              <White>Funding</White> / Countdown
            </StatLabel>
            <StatValue>{fundingAndCountdown}</StatValue>
          </StatsBlock>

          {/* <StatsBlock>
            <StatLabel>24h Volume ({base})</StatLabel>
            <StatValue>
              {formatNumberToSignificantDigits(ticker.baseVolume, 6)}
            </StatValue>
          </StatsBlock> */}

          <StatsBlock>
            <StatLabel>Open Interest</StatLabel>
            <StatDetailRed>{openInterestQuoteFormatted}</StatDetailRed>
          </StatsBlock>

          <StatsBlock>
            <StatLabel>24h Volume</StatLabel>
            <StatValue>
              ${formatNumberToSignificantDigits(ticker.quoteVolume, 6)}
            </StatValue>
          </StatsBlock>
        </DesktopWrap>
      )}
    </>
  );
}

export default MarketHeaderStats;
